// =============================================================
// index.ts — DigMolSDK 파사드 + 포맷 유틸
//  UI(main.ts)는 이 파사드만 import 한다.
//  지갑 어댑터 교체(injected → Reown)는 여기 한 줄만 바꾸면 끝.
// =============================================================

import { formatEther } from 'ethers';
import { InjectedWalletAdapter } from './injected';
// Reown 은 정적 import 하지 않는다.
//  · @reown/appkit 은 로드만으로도 무거운 사이드이펙트가 있어,
//    MetaMask 내장 브라우저(injected 경로)에서 불필요하게 로드되면
//    '연결 버튼 무반응' 을 악화시킬 수 있다.
//  · 실제로 Reown 이 필요한 경우에만 동적 import 한다(아래 create() 참고).
import { ChainClient } from './chain';
import { SwapService } from './swap';
import { AdminService } from './admin';
import { ParticipationFeed } from './feed';
import { ONE, PV_THRESHOLD, REOWN_PROJECT_ID } from './config';
import type { WalletAdapter } from './types';

export class DigMolSDK {
  readonly wallet: WalletAdapter;
  readonly chain: ChainClient;
  readonly swap: SwapService;
  readonly admin: AdminService;
  readonly feed: ParticipationFeed;

  constructor(adapter?: WalletAdapter) {
    // 기본은 injected 로 즉시 생성 (동기).
    //  · MetaMask 내장 브라우저 / 데스크톱 확장 → 이대로 즉시 연결.
    //  · Reown(QR/딥링크)이 필요한 환경은 정적 팩토리 create() 를 쓴다.
    this.wallet = adapter ?? new InjectedWalletAdapter();
    this.chain = new ChainClient(this.wallet);
    this.swap = new SwapService(this.wallet, this.chain);
    this.admin = new AdminService(this.wallet, this.chain);
    this.feed = new ParticipationFeed(); // 지갑 무관 독립 RPC
  }

  /**
   * 환경 감지 후 적절한 어댑터로 SDK 생성 (권장 진입점).
   *  · 주입 지갑 있음(MetaMask 내장 브라우저/확장) → injected (동기)
   *  · 주입 지갑 없음 + REOWN_PROJECT_ID 있음 → Reown 동적 로드 (QR/딥링크)
   *  · 그 외 → injected (연결 시 NO_WALLET 안내)
   *
   * main.ts:  const sdk = await DigMolSDK.create();
   */
  static async create(): Promise<DigMolSDK> {
    if (DigMolSDK.hasInjectedWallet()) {
      return new DigMolSDK(new InjectedWalletAdapter());
    }
    if (REOWN_PROJECT_ID) {
      // 여기서만 @reown/appkit 이 로드된다(코드 스플리팅).
      const { ReownWalletAdapter } = await import('./reown');
      return new DigMolSDK(new ReownWalletAdapter());
    }
    return new DigMolSDK(new InjectedWalletAdapter());
  }

  /** window.ethereum 주입 여부(멀티 프로바이더 배열 포함). SSR 안전. */
  private static hasInjectedWallet(): boolean {
    if (typeof window === 'undefined') return false;
    const eth = (window as unknown as { ethereum?: { providers?: unknown[] } }).ethereum;
    if (!eth) return false;
    if (Array.isArray(eth.providers)) return eth.providers.length > 0;
    return true;
  }
}

// ---- 포맷 유틸 ----

/** wei(18dec) → 정수 MOL 표시 문자열 (콤마) */
export function fmtMolInt(wei: bigint): string {
  return (wei / ONE).toLocaleString('en-US');
}

/** wei → 소수 표시 (BNB 등) */
export function fmtEther(wei: bigint | null | undefined, digits = 4): string {
  const n = Number(formatEther(wei ?? 0n));
  return n.toLocaleString('en-US', { maximumFractionDigits: digits });
}

/** Dener 서임 진척도 (0~100, 소수1자리) */
export function pvProgress(molWei: bigint): number {
  const whole = molWei / ONE;
  const pct = Number((whole * 10000n) / PV_THRESHOLD) / 100;
  return Math.min(100, pct);
}

/** 서임 달성 여부 */
export function isDener(molWei: bigint): boolean {
  return molWei / ONE >= PV_THRESHOLD;
}

/** 서임까지 남은 MOL (정수) */
export function remainingToDener(molWei: bigint): bigint {
  const whole = molWei / ONE;
  return whole >= PV_THRESHOLD ? 0n : PV_THRESHOLD - whole;
}

export * from './types';
export type { WalletRole, DistributorRow, BuyerRow } from './admin';
export type { FeedItem } from './feed';
export { hasAddress } from './chain';
export { PV_GATE_ADDRESS, CHAIN, PV_THRESHOLD, ONE } from './config';